import { useEffect, useRef, useState } from "react";
import { ArrowClockwise, X } from "@phosphor-icons/react";

const POLL_MS = 5 * 60 * 1000;      // background check every 5 min
const FOCUS_MIN_GAP_MS = 60 * 1000; // …and when the tab regains focus (at most once a minute)
const SNOOZE_MS = 30 * 60 * 1000;   // "Later" hides the bar for 30 min

// The served index.html embeds the build's script hashes, so its text changes on every deploy.
const fetchIndex = async () => {
  const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store", headers: { Accept: "text/html" } });
  if (!res.ok || !(res.headers.get("content-type") || "").includes("text/html")) return null;
  const html = await res.text();
  return html.includes("<script") ? html.replace(/\s+/g, " ").trim() : null;
};

// Small bar that appears when a new build has been deployed while this tab was open.
export function UpdateBanner() {
  const [show, setShow] = useState(false);
  const baseline = useRef(null);
  const lastCheck = useRef(0);
  const snoozedUntil = useRef(0);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      lastCheck.current = Date.now();
      try {
        const html = await fetchIndex();
        if (!alive || !html) return;
        if (baseline.current == null) baseline.current = html;
        else if (html !== baseline.current && Date.now() >= snoozedUntil.current) setShow(true);
      } catch { /* offline or blocked — try again next time */ }
    };
    check();
    const timer = setInterval(check, POLL_MS);
    const onFocus = () => { if (Date.now() - lastCheck.current > FOCUS_MIN_GAP_MS) check(); };
    const onVisible = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  if (!show) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-3 rounded-full border border-primary/40 bg-card/95 backdrop-blur px-4 py-2 shadow-lg animate-in slide-in-from-bottom-4 fade-in duration-300" role="status" data-testid="update-banner">
      <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
      <span className="text-sm">New version available — <button type="button" onClick={() => window.location.reload()} className="font-semibold text-primary underline underline-offset-2 hover:opacity-80" data-testid="update-banner-refresh">click to refresh</button></span>
      <button type="button" onClick={() => window.location.reload()} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:opacity-90" data-testid="update-banner-reload"><ArrowClockwise size={12} weight="bold" /> Refresh</button>
      <button type="button" onClick={() => { snoozedUntil.current = Date.now() + SNOOZE_MS; setShow(false); }} className="rounded-full p-1 text-muted-foreground hover:text-foreground" title="Later (30 min)" data-testid="update-banner-later"><X size={14} /></button>
    </div>
  );
}
